import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import type { User } from '@supabase/supabase-js'

interface UseAuthStateReturn {
  user: User | null
  loading: boolean
  connectionError: string | null
  isInvitePage: boolean
  hasOrganization: boolean | null
  isEmailVerified: boolean
  clearAuth: () => void
}

/**
 * Custom hook for managing authentication state
 * Extracts auth logic from App component to improve separation of concerns
 */
export const useAuthState = (): UseAuthStateReturn => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [isInvitePage, setIsInvitePage] = useState(false)
  const [hasOrganization, setHasOrganization] = useState<boolean | null>(null)
  const [isEmailVerified, setIsEmailVerified] = useState(false)

  /**
   * Selectively clear authentication-related data
   * Only removes Supabase auth keys, preserving other app data
   */
  const clearAuth = () => {
    // Clear Supabase-specific localStorage keys
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))

    // Clear sessionStorage
    sessionStorage.clear()

    setUser(null)
    setHasOrganization(null)
    setIsEmailVerified(false)
    setLoading(false)
  }

  const checkUserOrganization = async (authUser: User) => {
    try {
      const { data: userProfile } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', authUser.id)
        .single()

      setHasOrganization(!!userProfile?.org_id)
    } catch {
      setHasOrganization(false)
    }
  }

  useEffect(() => {
    // Check if we're on the invite page (doesn't require authentication)
    const currentPath = window.location.pathname
    const isInvite = currentPath === '/invite' || currentPath.includes('/invite')
    setIsInvitePage(isInvite)

    // Prevent infinite loading with timeout fallback
    const timeout = setTimeout(() => {
      setLoading(false)
    }, 3000)

    // Listen for Supabase auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null)
        // Only clear storage on explicit sign out, not on session expiry
        if (event === 'SIGNED_OUT') {
          localStorage.clear()
          sessionStorage.clear()
        }
      } else if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user)
        setIsEmailVerified(!!session.user.email_confirmed_at)
        checkUserOrganization(session.user)
      }
    })

    /**
     * Initialize authentication state
     * Attempts to get current session with timeout protection
     */
    const initAuth = async () => {
      try {
        setConnectionError(null)

        // Get current session with timeout protection
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Session timeout')), 2000)
        )

        const raceResult = await Promise.race([sessionPromise, timeoutPromise])
        const { data: { session }, error } = raceResult

        if (error) throw error

        if (session?.user) {
          setUser(session.user)
          setIsEmailVerified(!!session.user.email_confirmed_at)
          await checkUserOrganization(session.user)
        } else {
          setUser(null)
          setHasOrganization(null)
          setIsEmailVerified(false)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        if (errorMessage.includes('Failed to fetch')) {
          setConnectionError('Network connection failed. Please check your internet connection and try again.')
        } else if (errorMessage.includes('timeout')) {
          setConnectionError('Connection timeout. Please check your internet connection.')
        } else if (errorMessage.includes('Invalid API key') || errorMessage.includes('supabase')) {
          setConnectionError('Authentication service configuration error. Please check your environment variables.')
        }
        // Continue with unauthenticated state on auth failures
        setUser(null)
        setHasOrganization(null)
        setIsEmailVerified(false)
      } finally {
        clearTimeout(timeout)
        setLoading(false)
      }
    }

    initAuth()

    // Cleanup subscriptions on unmount
    return () => {
      clearTimeout(timeout)
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe()
      }
    }
  }, [])

  return {
    user,
    loading,
    connectionError,
    isInvitePage,
    hasOrganization,
    isEmailVerified,
    clearAuth
  }
}